import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Schedule moved to ws5 (apigroup Cleaner kjtchkL8) — 2026-05-25. Endpoints mirror the
// old ws1 contract 1:1 (paths + params unchanged, incl. the misspelled `acccept`), so this
// is a canonical-only switch. ws5 endpoints are public (no cleaner token) by design.
const FETCH_ENDPOINT = 'https://api.wiseclean.ee/api:kjtchkL8/cleaners_schedule';
const UPDATE_ENDPOINT = 'https://api.wiseclean.ee/api:kjtchkL8/cleaner_schedule';
const SUPPORT_EMAIL = 'info@wiseclean.eu';

const DAYS_OF_WEEK_RU = [
    'Воскресенье',
    'Понедельник',
    'Вторник',
    'Среда',
    'Четверг',
    'Пятница',
    'Суббота',
];

const STATUS_LABEL = {
    0: 'Не беру заказы',
    1: 'Беру заказы',
    2: 'Беру более одного',
};

const PROMO_DAYS_AHEAD = 22;
const WEEK_LENGTH = 7;

// Returns YYYY-MM-DD for the current moment in Europe/Tallinn timezone.
// Avoids the legacy bug where new Date().toISOString() shifted to UTC date and
// reported the wrong "tomorrow" near midnight Tallinn time.
function getTallinnDate(date = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Tallinn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

// Add N days to a YYYY-MM-DD string. Operates in UTC to avoid DST jumps.
function addDays(yyyyMmDd, days) {
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
}

function getDayOfWeek(yyyyMmDd) {
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function CleanerSchedule() {
    const [searchParams] = useSearchParams();
    const cleanerID = searchParams.get('cleanerID');

    const [step, setStep] = useState('checking');
    const [scheduleByDate, setScheduleByDate] = useState({});
    const [busyDate, setBusyDate] = useState(null);
    const [toast, setToast] = useState(null);

    useEffect(() => {
        if (!toast) return undefined;
        const id = setTimeout(() => setToast(null), 2500);
        return () => clearTimeout(id);
    }, [toast]);

    const todayTallinn = getTallinnDate();
    const minDate = addDays(todayTallinn, 1);
    const maxDate = addDays(todayTallinn, PROMO_DAYS_AHEAD);

    const [weekStart, setWeekStart] = useState(minDate);

    const indexSchedule = useCallback((arr) => {
        const map = {};
        if (Array.isArray(arr)) {
            arr.forEach((item) => {
                if (item && item.date) {
                    map[item.date] = item.AcceptOrders;
                }
            });
        }
        return map;
    }, []);

    const refetchSchedule = useCallback(async () => {
        if (!cleanerID) return;
        try {
            const r = await fetch(`${FETCH_ENDPOINT}?cleanerID=${encodeURIComponent(cleanerID)}`);
            if (!r.ok) throw new Error(`cleaners_schedule ${r.status}`);
            const arr = await r.json();
            setScheduleByDate(indexSchedule(arr));
        } catch (err) {
            // Network blip on refetch should not destroy the UI; keep last good state.
        }
    }, [cleanerID, indexSchedule]);

    useEffect(() => {
        if (!cleanerID) {
            setStep('error');
            return undefined;
        }
        let cancelled = false;
        fetch(`${FETCH_ENDPOINT}?cleanerID=${encodeURIComponent(cleanerID)}`)
            .then((r) => {
                if (!r.ok) throw new Error(`cleaners_schedule ${r.status}`);
                return r.json();
            })
            .then((arr) => {
                if (cancelled) return;
                setScheduleByDate(indexSchedule(arr));
                setStep('ready');
            })
            .catch(() => {
                if (!cancelled) setStep('error');
            });
        return () => {
            cancelled = true;
        };
    }, [cleanerID, indexSchedule]);

    const updateStatus = async (date, accept) => {
        if (busyDate) return;
        setBusyDate(date);
        try {
            // Backend contract uses misspelled "acccept" param (3× c). Do not "fix" — Xano expects it.
            const url = `${UPDATE_ENDPOINT}?cleanerID=${encodeURIComponent(cleanerID)}&date=${encodeURIComponent(date)}&acccept=${accept}`;
            const r = await fetch(url, { method: 'GET' });
            if (!r.ok) throw new Error(`cleaner_schedule ${r.status}`);
            await refetchSchedule();
            setToast({
                date,
                label: STATUS_LABEL[accept] || 'Сохранено',
            });
        } catch (err) {
            alert('Не удалось сохранить. Проверьте интернет и попробуйте ещё раз.');
        } finally {
            setBusyDate(null);
        }
    };

    const dates = [];
    {
        let cursor = weekStart;
        for (let i = 0; i < WEEK_LENGTH; i += 1) {
            if (cursor > maxDate) break;
            dates.push(cursor);
            cursor = addDays(cursor, 1);
        }
    }

    const canPrev = weekStart > minDate;
    const canNext = addDays(weekStart, WEEK_LENGTH) <= maxDate;

    const goPrev = () => {
        if (!canPrev) return;
        const proposed = addDays(weekStart, -WEEK_LENGTH);
        setWeekStart(proposed < minDate ? minDate : proposed);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const goNext = () => {
        if (!canNext) return;
        setWeekStart(addDays(weekStart, WEEK_LENGTH));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (step === 'error') {
        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.errorMain}>
                    <div style={styles.card}>
                        <h1 style={styles.errorTitle}>Не удалось загрузить</h1>
                        <p style={styles.errorBody}>
                            {cleanerID
                                ? 'Не получилось получить ваше расписание. Напишите нам, мы поможем.'
                                : 'Ссылка неполная — нет cleanerID. Откройте расписание из приложения или письма.'}
                        </p>
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.supportLink}>
                            Связаться · {SUPPORT_EMAIL}
                        </a>
                    </div>
                </main>
            </div>
        );
    }

    if (step === 'checking') {
        return (
            <div style={styles.page}>
                <header style={styles.header}>
                    <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
                </header>
                <main style={styles.main}>
                    <div style={styles.loadingText}>Загрузка расписания…</div>
                </main>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <style>{`
                @keyframes wc-toast-in {
                    from { opacity: 0; transform: translate(-50%, 16px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
            `}</style>
            <header style={styles.header}>
                <img src="/wiseclean-logo.png" alt="WiseClean" style={styles.logo} />
            </header>
            <main style={styles.main}>
                <div style={styles.card}>
                    <h1 style={styles.title}>Моё расписание</h1>
                    <p style={styles.subtitle}>
                        Отметьте, когда вы готовы брать заказы. Доступно на 22 дня вперёд от сегодня.
                    </p>

                    <button
                        type="button"
                        style={{ ...styles.navButton, ...(canPrev ? null : styles.navButtonDisabled) }}
                        onClick={goPrev}
                        disabled={!canPrev}
                    >
                        ← Предыдущие 7 дней
                    </button>

                    <div style={styles.daysList}>
                        {dates.map((date) => {
                            const status = scheduleByDate[date];
                            const dayName = DAYS_OF_WEEK_RU[getDayOfWeek(date)];
                            const statusText = (status === 0 || status === 1 || status === 2)
                                ? STATUS_LABEL[status]
                                : 'Не указано';
                            const isBusy = busyDate === date;

                            return (
                                <div key={date} style={styles.dayRow}>
                                    <div style={styles.dayHeader}>
                                        <div style={styles.dayDate}>{date}</div>
                                        <div style={styles.dayName}>{dayName}</div>
                                        <div style={styles.dayStatus}>
                                            Статус: <strong>{statusText}</strong>
                                        </div>
                                    </div>
                                    <div style={styles.buttonRow}>
                                        <button
                                            type="button"
                                            style={getButtonStyle('cannot', status === 0)}
                                            onClick={() => updateStatus(date, 0)}
                                            disabled={isBusy}
                                        >
                                            Не беру
                                        </button>
                                        <button
                                            type="button"
                                            style={getButtonStyle('accept', status === 1)}
                                            onClick={() => updateStatus(date, 1)}
                                            disabled={isBusy}
                                        >
                                            Беру
                                        </button>
                                        <button
                                            type="button"
                                            style={getButtonStyle('multi', status === 2)}
                                            onClick={() => updateStatus(date, 2)}
                                            disabled={isBusy}
                                        >
                                            Беру более одного
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        style={{ ...styles.navButton, ...(canNext ? null : styles.navButtonDisabled) }}
                        onClick={goNext}
                        disabled={!canNext}
                    >
                        Следующие 7 дней →
                    </button>
                </div>
            </main>

            {toast && (
                <div
                    role="status"
                    aria-live="polite"
                    style={styles.toast}
                >
                    <span style={styles.toastCheck} aria-hidden="true">✓</span>
                    <span>
                        Сохранено: <strong>{toast.date}</strong> — {toast.label}
                    </span>
                </div>
            )}
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--wc-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'center',
    },
    logo: {
        height: '36px',
        width: 'auto',
        objectFit: 'contain',
    },
    main: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '8px 16px 40px',
    },
    errorMain: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    loadingText: {
        color: 'var(--wc-text-secondary)',
        fontSize: '15px',
        padding: '40px 20px',
    },
    card: {
        width: '100%',
        maxWidth: '640px',
        background: 'var(--wc-bg-primary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-card)',
        boxShadow: 'var(--wc-shadow-card)',
        padding: '24px 20px',
        boxSizing: 'border-box',
    },
    title: {
        margin: '0 0 4px',
        fontSize: '22px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    subtitle: {
        margin: '0 0 20px',
        fontSize: '14px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.45,
    },
    navButton: {
        display: 'block',
        width: '100%',
        background: 'var(--wc-brand)',
        color: 'var(--wc-on-brand)',
        border: 'none',
        borderRadius: 'var(--wc-radius-btn)',
        padding: '14px 16px',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        boxShadow: 'var(--wc-shadow-cta)',
        marginBottom: '16px',
        marginTop: '8px',
    },
    navButtonDisabled: {
        background: 'var(--wc-bg-secondary)',
        color: 'var(--wc-text-tertiary)',
        cursor: 'not-allowed',
        boxShadow: 'none',
    },
    daysList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '8px',
    },
    dayRow: {
        background: 'var(--wc-bg-secondary)',
        border: '1px solid var(--wc-border)',
        borderRadius: 'var(--wc-radius-input)',
        padding: '12px 14px',
    },
    dayHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '8px',
        marginBottom: '10px',
    },
    dayDate: {
        fontSize: '15px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    dayName: {
        fontSize: '14px',
        color: 'var(--wc-text-secondary)',
    },
    dayStatus: {
        marginLeft: 'auto',
        fontSize: '13px',
        color: 'var(--wc-text-secondary)',
    },
    buttonRow: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    statusButton: {
        flex: '1 1 0',
        minWidth: '110px',
        padding: '10px 12px',
        borderRadius: 'var(--wc-radius-btn)',
        fontSize: '13px',
        fontWeight: 600,
        fontFamily: 'inherit',
        cursor: 'pointer',
        border: '1.5px solid transparent',
        background: 'var(--wc-bg-primary)',
        color: 'var(--wc-text-primary)',
        opacity: 0.8,
        transition: 'opacity 150ms',
    },
    /* Active/idle status colors are computed in getButtonStyle() below. */
    errorTitle: {
        margin: '0 0 12px',
        fontSize: '20px',
        fontWeight: 700,
        color: 'var(--wc-text-primary)',
    },
    errorBody: {
        fontSize: '15px',
        color: 'var(--wc-text-secondary)',
        lineHeight: 1.5,
        margin: '0 0 16px',
    },
    supportLink: {
        color: 'var(--wc-brand-dark)',
        fontWeight: 600,
        textDecoration: 'none',
        fontSize: '15px',
    },
    toast: {
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--wc-success)',
        color: 'var(--wc-on-brand)',
        padding: '12px 18px',
        borderRadius: 'var(--wc-radius-btn)',
        fontSize: '14px',
        fontWeight: 600,
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.18)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        maxWidth: 'calc(100vw - 32px)',
        animation: 'wc-toast-in 200ms var(--wc-ease)',
    },
    toastCheck: {
        fontSize: '16px',
        fontWeight: 800,
        lineHeight: 1,
    },
};

const STATUS_PALETTE = {
    cannot: 'var(--wc-error)',
    accept: 'var(--wc-success)',
    multi: 'var(--wc-success-dark)',
};

function getButtonStyle(kind, isActive) {
    const tone = STATUS_PALETTE[kind];
    if (isActive) {
        return {
            ...styles.statusButton,
            background: tone,
            borderColor: tone,
            color: 'var(--wc-on-brand)',
            opacity: 1,
        };
    }
    return {
        ...styles.statusButton,
        borderColor: tone,
        color: tone,
    };
}

export default CleanerSchedule;
